// ═══════════════════════════════════════════════════════════
// i18n — Arabic / English language context
// ═══════════════════════════════════════════════════════════

import { createContext, useContext, useEffect, useState, ReactNode } from "react";

export type Lang = "ar" | "en";

interface Strings {
  // Brand
  orgEyebrow: string;
  orgTitleLine1: string;
  orgTitleLine2: string;
  // Stats
  statFloors: string;
  statDepts: string;
  statEntries: string;
  // Search bar
  searchPlaceholder: string;
  searchPlaceholderShort: string;
  // Quick actions
  browseAllFloors: string;
  browseAllFloorsSub: string;
  advancedSearch: string;
  advancedSearchSub: string;
  // Fire notice
  fireNotice: string;
  fireNoticeEn: string;
  // Nav
  navHome: string;
  navSearch: string;
  navBrowse: string;
  // Search view
  searchTitle: string;
  searchTitleEn: string;
  searchAll: string;
  searchDept: string;
  searchHead: string;
  searchFloor: string;
  searchResultCount: (n: number) => string;
  searchNoResults: string;
  searchNoResultsEn: string;
  searchExamples: string;
  // Browse view
  browseTitle: string;
  browseSubtitle: (n: number) => string;
  floorUnit: string;
  // Detail view
  back: string;
  floorBadgeLabel: string;
  manager: string;
  otherDeptsOnFloor: string;
  contactOffice: string;
  contactMobile: string;
  contactEmail: string;
  contactOfficeLabel: string;
  contactMobileLabel: string;
  saveContact: string;
  shareWhatsApp: string;
  openOutlookWeb: string;
  qrTitle: string;
  qrSubtitle: string;
  printDirectory: string;
  printDirectorySub: string;
  langSwitch: string; // label for the toggle button (shows the OTHER language)
}

const AR: Strings = {
  orgEyebrow: "ديوان الخدمة المدنية والتطوير الحكومي",
  orgTitleLine1: "ديوان الخدمة المدنية",
  orgTitleLine2: "والتطوير الحكومي",
  statFloors: "طابق",
  statDepts: "إدارة",
  statEntries: "مُدخل",
  searchPlaceholder: "ابحث بالإدارة، المسؤول، رقم الطابق...",
  searchPlaceholderShort: "ابحث... | Search...",
  browseAllFloors: "تصفح جميع الطوابق",
  browseAllFloorsSub: "عرض كامل من الطابق 52 حتى البدروم",
  advancedSearch: "بحث متقدم",
  advancedSearchSub: "بحث بالاسم أو المسؤول أو رقم الطابق",
  fireNotice: "في حالة الحريق يرجى استخدام السلالم",
  fireNoticeEn: "In Case of Fire — Use Stairs",
  navHome: "الرئيسية",
  navSearch: "بحث",
  navBrowse: "الطوابق",
  searchTitle: "البحث في الدليل",
  searchTitleEn: "SEARCH DIRECTORY",
  searchAll: "الكل",
  searchDept: "إدارة",
  searchHead: "مسؤول",
  searchFloor: "طابق",
  searchResultCount: (n) => `${n} نتيجة`,
  searchNoResults: "لا توجد نتائج",
  searchNoResultsEn: "No results found",
  searchExamples: "أمثلة للبحث",
  browseTitle: "جميع الطوابق",
  browseSubtitle: (n) => `ALL ${n} LEVELS — Floor 52 → B2`,
  floorUnit: "طابق",
  back: "رجوع",
  floorBadgeLabel: "طابق",
  manager: "المدير",
  otherDeptsOnFloor: "إدارات أخرى في نفس الطابق",
  contactOffice: "مكتب",
  contactMobile: "جوال",
  contactEmail: "بريد",
  contactOfficeLabel: "مكتب",
  contactMobileLabel: "جوال",
  saveContact: "حفظ في جهات الاتصال",
  shareWhatsApp: "مشاركة عبر واتساب",
  openOutlookWeb: "فتح في Outlook Web",
  qrTitle: "رمز QR للإدارة",
  qrSubtitle: "امسح الرمز لفتح صفحة الإدارة مباشرة",
  printDirectory: "تثبيت التطبيق على الشاشة الرئيسية",
  printDirectorySub: "خطوات إضافة التطبيق لـ iPhone و أندرويد",
  langSwitch: "EN",
};

const EN: Strings = {
  orgEyebrow: "Civil Service & Government Development Bureau",
  orgTitleLine1: "Civil Service &",
  orgTitleLine2: "Government Development Bureau",
  statFloors: "Floors",
  statDepts: "Depts",
  statEntries: "Entries",
  searchPlaceholder: "Search by department, head, or floor…",
  searchPlaceholderShort: "Search… | ابحث…",
  browseAllFloors: "Browse all floors",
  browseAllFloorsSub: "Full tour from Floor 52 to Basement",
  advancedSearch: "Advanced search",
  advancedSearchSub: "Search by name, head, or floor number",
  fireNotice: "In Case of Fire — Use Stairs",
  fireNoticeEn: "في حالة الحريق يرجى استخدام السلالم",
  navHome: "Home",
  navSearch: "Search",
  navBrowse: "Floors",
  searchTitle: "Search Directory",
  searchTitleEn: "البحث في الدليل",
  searchAll: "All",
  searchDept: "Dept",
  searchHead: "Head",
  searchFloor: "Floor",
  searchResultCount: (n) => `${n} result${n === 1 ? "" : "s"}`,
  searchNoResults: "No results found",
  searchNoResultsEn: "لا توجد نتائج",
  searchExamples: "Try searching for",
  browseTitle: "All Floors",
  browseSubtitle: (n) => `ALL ${n} LEVELS — Floor 52 → B2`,
  floorUnit: "Floors",
  back: "Back",
  floorBadgeLabel: "Floor",
  manager: "Head",
  otherDeptsOnFloor: "Other departments on this floor",
  contactOffice: "Office",
  contactMobile: "Mobile",
  contactEmail: "Email",
  contactOfficeLabel: "Office",
  contactMobileLabel: "Mobile",
  saveContact: "Save to Contacts",
  shareWhatsApp: "Share via WhatsApp",
  openOutlookWeb: "Open in Outlook Web",
  qrTitle: "Department QR code",
  qrSubtitle: "Scan to open this department's page",
  printDirectory: "Install App on Home Screen",
  printDirectorySub: "Step-by-step guide for iPhone & Android",
  langSwitch: "ع",
};

interface Ctx {
  lang: Lang;
  t: Strings;
  toggleLang: () => void;
  setLang: (l: Lang) => void;
  isRtl: boolean;
}

const LangContext = createContext<Ctx | null>(null);

// Language follows the device / OS setting LIVE.
// Arabic-locale device → Arabic UI. Everything else → English.
// The toggle is session-only — refresh re-reads the system.
function detectSystemLang(): Lang {
  if (typeof window === "undefined") return "ar";
  const nav = window.navigator;
  const candidates: string[] = [
    ...(nav.languages || []),
    nav.language,
  ].filter(Boolean).map((s) => s.toLowerCase());
  for (const c of candidates) {
    if (c.startsWith("ar")) return "ar";
    if (c.startsWith("en")) return "en";
  }
  return "en";
}

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectSystemLang);

  useEffect(() => {
    const html = document.documentElement;
    html.lang = lang;
    html.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  // Listen for live system language changes (rare but supported)
  useEffect(() => {
    const handler = () => setLangState(detectSystemLang());
    window.addEventListener("languagechange", handler);
    return () => window.removeEventListener("languagechange", handler);
  }, []);

  const value: Ctx = {
    lang,
    t: lang === "ar" ? AR : EN,
    isRtl: lang === "ar",
    setLang: setLangState,
    toggleLang: () => setLangState((l) => (l === "ar" ? "en" : "ar")),
  };

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang(): Ctx {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used inside LangProvider");
  return ctx;
}
